/**
 * 📚 The Library — General Cari NPC page
 *
 * Cari is a pitbull granny librarian who walks new players through the game's
 * systems. She "forgets" mid-explanation and asks the player to find a Lost
 * Tome out in the world to remind her. V1 ships the dialog scaffold; the
 * Tombs that drop those tomes ship in a later phase.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


export default function LibraryPage() {
  const { isAuthed } = useAuth();
  const [state, setState] = useState(null);
  const [villainCodex, setVillainCodex] = useState(null);
  const [activeTopic, setActiveTopic] = useState(null);
  const [phase, setPhase] = useState("intro");  // intro | forget | resume
  const [busy, setBusy] = useState(false);
  // 📜 Onboarding quest state — for the journal-aware Cari banner
  const [questState, setQuestState] = useState(null);
  const [cariJournalReply, setCariJournalReply] = useState(null);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/library/state`);
      setState(r.data);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error("Cari isn't in the library right now.");
    }
  };
  const refreshVillainCodex = async () => {
    try {
      const r = await axios.get(`${API}/library/villains`);
      setVillainCodex(r.data);
    } catch {}
  };
  const refreshQuest = async () => {
    try {
      const r = await axios.get(`${API}/onboarding/state`);
      setQuestState(r.data);
    } catch {}
  };
  useEffect(() => {
    if (isAuthed) { refresh(); refreshQuest(); refreshVillainCodex(); }
  }, [isAuthed]);

  const onShowJournalToCari = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/onboarding/ask-npc`, { npc_id: "cari" });
      setCariJournalReply(r.data.npc);
      setQuestState(r.data.state);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cari isn't ready.");
    } finally { setBusy(false); }
  };

  const onAskCariForBread = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/onboarding/cari/bread`);
      setQuestState(r);
      toast.success("🍞 Cari handed you a fresh loaf!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cari shook her head.");
    } finally { setBusy(false); }
  };

  const openTopic = (t) => {
    setActiveTopic(t);
    // Skip "forget" phase if no tome required OR player already has it
    if (!t.forget || t.has_required_tome) {
      setPhase(t.completed ? "resume" : "intro");
    } else {
      setPhase("intro");
    }
  };

  const advancePhase = async () => {
    if (!activeTopic) return;
    if (phase === "intro") {
      // If topic has no forget phase OR player has the tome, jump to resume
      if (!activeTopic.forget || activeTopic.has_required_tome) {
        await markComplete();
        return;
      }
      setPhase("forget");
      return;
    }
    if (phase === "forget") {
      // Player closes the modal without the tome — they need to go find it
      setActiveTopic(null);
      return;
    }
    if (phase === "resume") {
      await markComplete();
    }
  };

  const markComplete = async () => {
    if (!activeTopic) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/library/complete-topic`, { topic_id: activeTopic.id });
      setState(r.data);
      if (!r.data.already_completed) {
        toast.success(`📚 Lesson noted — ${activeTopic.title}`);
      }
      setActiveTopic(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cari got distracted.");
    } finally {
      setBusy(false);
    }
  };

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1a0010] via-black to-[#10001a] text-amber-100">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <div className="text-6xl mb-4">📚</div>
          <h1 className="text-4xl font-black mb-2" style={{ fontFamily: "Anton, sans-serif" }}>
            CARI'S ATHENAEUM
          </h1>
          <p className="text-neutral-400">Sign in to visit General Cari.</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0010] via-black to-[#10001a] text-amber-100" data-testid="library-page">
      <Header />

      <main className="max-w-6xl mx-auto px-4 py-6 pb-24">
        {/* Cari hero card */}
        <div className="rounded-2xl border border-purple-700/40 bg-gradient-to-br from-[#10001a] via-black/60 to-[#1a0010] p-6 mb-6 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 text-[160px] opacity-10 select-none">📚</div>
          <div className="relative flex flex-col md:flex-row gap-5 items-start">
            <div className="text-7xl select-none flex-shrink-0">🐕</div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.4em] text-purple-300/70 mb-1">Town Librarian</div>
              <h1 className="text-4xl md:text-5xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
                GENERAL CARI
              </h1>
              <div className="text-xs text-neutral-500 mt-1">🐕 Pitbull · sweet, wise, occasionally forgetful</div>
              {state && (
                <p className="text-sm text-amber-100/90 italic mt-3 leading-relaxed max-w-2xl" data-testid="cari-greeting">
                  "{state.npc.greeting}"
                </p>
              )}
            </div>
            {state && (
              <div className="text-right text-xs text-neutral-400 flex-shrink-0">
                <div data-testid="library-topics-progress">
                  📖 Lessons: <span className="text-amber-300 font-black">{state.topics_completed}</span>
                  <span className="text-neutral-600"> / {state.topics_total}</span>
                </div>
                <div className="mt-1" data-testid="library-tomes-count">
                  🪦 Tomes: <span className="text-amber-300 font-black">{state.inventory.tomes_count}</span>
                </div>
                <div className="mt-1" data-testid="library-books-count">
                  📕 Books: <span className="text-amber-300 font-black">{state.inventory.books_count}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 📜 Journal-aware Cari banner — appears when player is on the onboarding quest */}
        {questState?.journal && (
          <CariJournalBanner
            questState={questState}
            cariReply={cariJournalReply}
            busy={busy}
            onShow={onShowJournalToCari}
            onAskBread={onAskCariForBread}
          />
        )}

        {/* Tutorial topics list */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-2xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
              📖 WHAT WOULD YOU LIKE TO KNOW?
            </h2>
            <div className="h-px flex-1 bg-gradient-to-r from-purple-700/60 to-transparent" />
          </div>

          {!state ? (
            <div className="text-neutral-500 text-sm">Cari is putting on the kettle...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {state.topics.map((t) => (
                <TopicCard key={t.id} topic={t} onClick={() => openTopic(t)} />
              ))}
            </div>
          )}
        </section>

        {/* 📚🪞 Restricted Shelf — Villain Codex (gated by Discoverability Rule) */}
        {villainCodex && villainCodex.entries && villainCodex.entries.length > 0 && (
          <section className="mt-10" data-testid="library-restricted-shelf">
            <div className="mb-3 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-2xl font-black text-purple-300" style={{ fontFamily: "Anton, sans-serif" }}>
                  🪞 THE RESTRICTED SHELF
                </h2>
                <p className="text-[11px] text-purple-200/60 italic mt-1">
                  Entries you've stumbled across. Cari keeps the dangerous ones in the back.
                </p>
              </div>
              <div className="text-[11px] text-purple-300/70 font-mono" data-testid="restricted-shelf-counter">
                {villainCodex.total_known} / {villainCodex.total_in_archive} known
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {villainCodex.entries.map((v) => (
                <VillainEntryCard key={v.id} entry={v} />
              ))}
            </div>
            {villainCodex.meta_unlocked && (
              <div className="mt-4 rounded-xl border-2 border-amber-500/60 bg-amber-900/15 p-4" data-testid="restricted-shelf-meta-unlock">
                <div className="text-xs text-amber-300/90 font-black uppercase tracking-widest">⚠ A pattern, recognized</div>
                <p className="text-sm text-amber-100/85 italic font-serif mt-1">
                  "Every one of them confides in him. And he never tells. He doesn't even know he's the channel."
                </p>
              </div>
            )}
          </section>
        )}

        {/* Phase 2 hint */}
        <div className="mt-8 text-center text-xs text-neutral-500 italic">
          🪦 Lost Tombs scattered across the kingdom will drop the Tomes Cari needs. Coming soon to the Wilds.
        </div>
      </main>

      {/* Dialog modal */}
      {activeTopic && (
        <CariDialog
          topic={activeTopic}
          phase={phase}
          busy={busy}
          onAdvance={advancePhase}
          onClose={() => setActiveTopic(null)}
        />
      )}

      <Footer />
    </div>
  );
}


function TopicCard({ topic, onClick }) {
  const isCompleted = topic.state === "completed";
  const isLocked = topic.state === "locked_needs_tome";
  const cls = isCompleted
    ? "border-amber-500/60 bg-amber-500/5 shadow-[0_0_18px_rgba(245,158,11,0.12)]"
    : isLocked
      ? "border-red-900/40 bg-black/40 grayscale opacity-70"
      : "border-purple-700/40 bg-gradient-to-br from-[#10001a] to-black/60 hover:border-amber-500 hover:shadow-[0_0_20px_rgba(245,158,11,0.18)] cursor-pointer";
  return (
    <button onClick={onClick}
            data-testid={`library-topic-${topic.id}`}
            className={`rounded-xl border p-4 text-left transition ${cls}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-3xl">{topic.icon}</div>
        {isCompleted && (
          <div className="px-2 py-0.5 rounded-full bg-amber-500 text-black text-[10px] font-black"
               data-testid={`library-topic-${topic.id}-status`}>
            ✔ LEARNED
          </div>
        )}
        {isLocked && (
          <div className="px-2 py-0.5 rounded-full bg-red-900/70 text-red-200 text-[10px] font-black"
               data-testid={`library-topic-${topic.id}-locked`}>
            🪦 NEEDS TOME
          </div>
        )}
      </div>
      <div className="text-base font-black text-amber-200 mt-2">{topic.title}</div>
      <div className="text-[11px] text-neutral-500 mt-1 line-clamp-2">
        {isLocked
          ? `Cari can't remember without ${topic.tome_required}.`
          : isCompleted
            ? "You've heard this one. Tap to revisit."
            : "Cari has something to share."}
      </div>
    </button>
  );
}


function CariDialog({ topic, phase, busy, onAdvance, onClose }) {
  let body = topic.intro;
  let cta = "Go on, dearie...";
  if (phase === "forget") { body = topic.forget; cta = "I'll go find it"; }
  if (phase === "resume") { body = topic.resume; cta = "Got it. Thanks, Cari."; }
  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
         data-testid="library-cari-dialog">
      <div className="relative w-full max-w-2xl rounded-2xl border border-purple-700/50 bg-gradient-to-br from-[#1a0010] via-black to-[#10001a] p-6">
        <button onClick={onClose}
                data-testid="library-dialog-close"
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 hover:bg-amber-500/30 flex items-center justify-center text-amber-300 text-lg">
          ×
        </button>

        <div className="flex items-start gap-4 mb-5">
          <div className="text-6xl flex-shrink-0">🐕</div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.3em] text-purple-300/70">General Cari</div>
            <div className="text-xl font-black text-amber-200">{topic.icon} {topic.title}</div>
          </div>
        </div>

        <div className="rounded-xl border border-neutral-800 bg-black/40 p-5 mb-5"
             data-testid="library-dialog-body">
          <p className="text-base text-amber-100/95 leading-relaxed italic">"{body}"</p>
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
            {phase === "intro" && "She's just getting started"}
            {phase === "forget" && "She's lost her train of thought"}
            {phase === "resume" && "She remembered, dear thing"}
          </div>
          <button onClick={onAdvance}
                  disabled={busy}
                  data-testid="library-dialog-advance"
                  className="px-5 py-2 rounded bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-bold text-sm">
            {cta}
          </button>
        </div>
      </div>
    </div>
  );
}



/**
 * 📜 Banner shown to players carrying Cari's Family Journal — gives them a
 * way to ask her about it (she'll shrug) AND to claim a 🍞 loaf for Dave.
 */
function CariJournalBanner({ questState, cariReply, busy, onShow, onAskBread }) {
  const stage = questState?.stage;
  const breadGiven = !!questState?.sub_quest_progress?.loaf_of_bread;
  const needsBread = ["dave_overwhelmed", "gathering"].includes(stage) && !breadGiven;
  return (
    <div className="rounded-2xl border-2 border-amber-700/60 bg-gradient-to-br from-amber-900/15 to-black/40 p-5 mb-6" data-testid="cari-journal-banner">
      <div className="flex items-start gap-4">
        <div className="text-5xl flex-shrink-0">📜</div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-amber-300/70">Active Quest</div>
          <div className="text-lg font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
            CARI'S FAMILY JOURNAL
          </div>
          <p className="text-sm text-amber-100/85 mt-2 italic font-serif">
            "Ask around town, sweetling. Someone with younger eyes — or hungrier ones — will know what it says."
          </p>

          {/* Cari's own reaction when shown the journal */}
          {!cariReply ? (
            <button
              onClick={onShow}
              disabled={busy}
              data-testid="show-cari-journal-btn"
              className="mt-3 px-4 py-1.5 rounded bg-purple-700 hover:bg-purple-600 disabled:opacity-50 text-white font-bold text-xs"
            >
              📜 Show Cari the Journal (again)
            </button>
          ) : (
            <div className="mt-3 rounded-lg border border-purple-700/50 bg-black/40 p-4">
              <div className="text-[10px] uppercase tracking-widest text-purple-300/70 mb-1">{cariReply.title}</div>
              <div className="space-y-1.5 text-sm text-amber-100/95 font-serif">
                {cariReply.lines.map((l, i) => <p key={i}>{l}</p>)}
              </div>
              <div className="mt-3 text-[11px] text-amber-300/80 italic">
                → Cari suggests asking Dave, Roc, Weasley, or Taylor.
              </div>
            </div>
          )}

          {/* Dave's sub-quest needs bread — Cari has some */}
          {needsBread && (
            <div className="mt-4 rounded-lg border-2 border-amber-500/60 bg-amber-900/15 p-4">
              <div className="text-xs text-amber-300/80 font-black mb-1">🍞 Looking for bread for Dave?</div>
              <p className="text-sm text-amber-100/90 italic font-serif mb-3">
                "Oh, I've got an extra loaf, sweetling. I feed the birds in the garden — they always
                leave me with too much. Here, take it."
              </p>
              <button
                onClick={onAskBread}
                disabled={busy}
                data-testid="ask-cari-bread-btn"
                className="px-4 py-1.5 rounded bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-black text-xs"
              >
                🍞 Accept the loaf
              </button>
            </div>
          )}
          {needsBread === false && stage === "dave_overwhelmed" && breadGiven && (
            <div className="mt-4 text-[11px] text-emerald-300/80 italic">
              ✔ Cari already gave you a loaf. Now go find a 🥚 basic egg at the Nest.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * 🪞 Villain codex card. Renders differently for redacted vs unlocked state.
 * Redacted = silhouette + ███ title + cryptic hint. Unlocked = full title +
 * summary + discovery vector. Locked entries are not rendered (backend filters
 * them out per the Discoverability Rule).
 */
function VillainEntryCard({ entry }) {
  const isRedacted = entry.state === "redacted";
  const cls = isRedacted
    ? "border-red-900/50 bg-black/70"
    : "border-purple-700/50 bg-gradient-to-br from-[#10001a] via-black to-[#1a000a] shadow-[0_0_18px_rgba(168,85,247,0.15)]";
  return (
    <div
      className={`rounded-xl border p-4 transition ${cls}`}
      data-testid={`villain-entry-${entry.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`text-4xl ${isRedacted ? "opacity-40 grayscale" : ""}`}>
          {entry.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div
            className={`text-xs font-black tracking-widest ${
              isRedacted ? "text-red-300/70 font-mono" : "text-purple-300"
            }`}
            data-testid={`villain-entry-${entry.id}-title`}
          >
            {entry.title}
          </div>
          {isRedacted ? (
            <p className="text-[11px] text-red-200/60 italic font-serif mt-2 leading-snug">
              {entry.hint}
            </p>
          ) : (
            <>
              <p className="text-[12px] text-amber-100/85 font-serif mt-2 leading-snug">
                {entry.summary}
              </p>
              {entry.discovery_vector && (
                <div className="text-[10px] text-purple-300/50 italic mt-2">
                  ↳ {entry.discovery_vector}
                </div>
              )}
            </>
          )}
        </div>
        <div
          className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest flex-shrink-0 ${
            isRedacted
              ? "bg-red-900/60 text-red-200"
              : "bg-purple-600 text-white"
          }`}
          data-testid={`villain-entry-${entry.id}-state`}
        >
          {isRedacted ? "🔒 Redacted" : "📖 Unlocked"}
        </div>
      </div>
    </div>
  );
}
